import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'clsx';
import { Container } from '@plone/components';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import {
  isExternalHref,
  resolveLinkHref,
  type LinkFieldValue,
} from '../../../helpers/blocks';
import arrowRightSVG from '../../../icons/arrow-right.svg';
import closeSVG from '../../../icons/close.svg';

const messages = defineMessages({
  defaultLabel: {
    id: 'announcementBar.default.label',
    defaultMessage: 'News:',
  },
  defaultLinkLabel: {
    id: 'announcementBar.default.linkLabel',
    defaultMessage: 'Learn more',
  },
  placeholder: {
    id: 'announcementBar.placeholder',
    defaultMessage: 'Type the announcement in the sidebar…',
  },
  close: {
    id: 'announcementBar.close',
    defaultMessage: 'Dismiss announcement',
  },
});

export type AnnouncementBarData = {
  label?: string;
  message?: string;
  link?: LinkFieldValue;
  linkLabel?: string;
  closeable?: boolean;
};

// A dispensa vale para a sessão do navegador e é amarrada ao texto: quando a
// equipe publica uma novidade diferente, a faixa volta a aparecer.
const STORAGE_KEY = 'odc.announcementBar.dismissed';

const readDismissed = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (message: string) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, message);
  } catch {
    // Armazenamento bloqueado (modo privado etc.): a faixa some só nesta view.
  }
};

type AnnouncementBarBodyProps = {
  data: AnnouncementBarData;
  isEditMode?: boolean;
};

const AnnouncementBarBody = ({
  data,
  isEditMode,
}: AnnouncementBarBodyProps) => {
  const intl = useIntl();
  const message = data.message?.trim() || '';
  const [dismissed, setDismissed] = useState(false);

  // Lê o storage só depois de montar: no SSR a faixa sempre sai renderizada e
  // o React não pode discordar disso na hidratação.
  useEffect(() => {
    if (isEditMode || !message) return;
    setDismissed(readDismissed() === message);
  }, [isEditMode, message]);

  if (!message && !isEditMode) return null;
  if (dismissed) return null;

  const label = data.label?.trim() || intl.formatMessage(messages.defaultLabel);
  const linkLabel =
    data.linkLabel?.trim() || intl.formatMessage(messages.defaultLinkLabel);
  const href = resolveLinkHref(data.link);

  const content = (
    <>
      <span className="announcement-bar__text">
        <strong className="announcement-bar__label">{label}</strong>{' '}
        {message || (
          <em className="announcement-bar__placeholder">
            {intl.formatMessage(messages.placeholder)}
          </em>
        )}
      </span>
      {href && (
        <span className="announcement-bar__cta">
          <span className="announcement-bar__cta-label">{linkLabel}</span>
          <Icon name={arrowRightSVG} size="20px" ariaHidden />
        </span>
      )}
    </>
  );

  const onClose = () => {
    if (isEditMode) return;
    writeDismissed(message);
    setDismissed(true);
  };

  return (
    <div className="block-announcement-bar">
      {/* Texto na coluna do site (mesmo Container do header); o X fica fora
          dela, no canto da tela, longe do "Saiba mais". */}
      <div
        className={cx('announcement-bar', {
          'announcement-bar--closeable': data.closeable,
        })}
      >
        <Container>
          {href && !isEditMode ? (
            <a
              className="announcement-bar__body announcement-bar__body--link"
              href={href}
              {...(isExternalHref(href) && {
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
            >
              {content}
            </a>
          ) : (
            // No editor o link não navega: o clique seleciona o bloco.
            <div className="announcement-bar__body">{content}</div>
          )}
        </Container>

        {data.closeable && (
          <button
            type="button"
            className="announcement-bar__close"
            aria-label={intl.formatMessage(messages.close)}
            onClick={onClose}
          >
            <Icon name={closeSVG} size="20px" ariaHidden />
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBarBody;
